/**
 * Soft≠Live Page179 base + Page225 dual residual (track_plane ∥ act_plane).
 * Mount: map-native person pin open (OpsMap person state).
 * Buses (isolated — each fails closed, no Soft invent cascade):
 *   GET /ops/person/status (always-on Live posture · dual track ∥ act)
 *   GET /personnel when track_plane (roster)
 *   GET /devices when track_plane (register labels)
 *   GET /personnel/policy when track_plane (track_available gate)
 *   GET /personnel/{u}/track when track_plane + policy armed + Trail tab
 *   POST /responder-dispatches · transition cancelled when act_plane open
 * Soft residual deleted:
 *  - useLive Soft dual-path without posture SoT
 *  - Soft invent person / ETA / skills / battery / track fixes
 * Dual path: track (read/rank/trail) ∥ act (dispatch write/admin floor)
 *
 * Rollback: drop dual residual + dual keys; pins previous pair (v324/206).
 */
import { useEffect, useMemo, useState } from "react";
import { html, M, F, Ico, KV, Alert, SOFT, stop, go } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { personnelLiveMapRowOf, personnelTrackQueryOf } from "./response-live.js";
import { initialsOf } from "./session.js";
import { formatUserInstant } from "./time.js";
import { operatorSay, looksLikeCode, boardLabelSay } from "./operator-say.js";

const ROLE_GLYPH = {
  owner: "shield", operator: "user", viewer: "eye", admin: "shield", user: "user",
};

const R = 6371000;
const rad = (x) => x * Math.PI / 180;
const UNKNOWN = "—";

/** Soft≠Live: missing contract ops must not crash the sheet. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_person_status") return "/ops/person/status";
    if (op === "get__personnel") return "/personnel";
    if (op === "get__personnel_policy") return "/personnel/policy";
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q)
        .filter(([, v]) => v != null && String(v) !== "")
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    if (op === "get__personnel_p_track") {
      const u = opts && opts.pathParams && opts.pathParams[0];
      const q = (opts && opts.query) || {};
      if (!u) return "";
      const parts = new URLSearchParams();
      if (q.limit != null && String(q.limit) !== "") parts.set("limit", String(q.limit));
      if (q.since) parts.set("since", String(q.since));
      if (q.until) parts.set("until", String(q.until));
      const qs = parts.toString();
      return `/personnel/${encodeURIComponent(u)}/track${qs ? `?${qs}` : ""}`;
    }
    if (op === "post__responder-dispatches") return "/responder-dispatches";
    if (op === "post__responder-dispatches_p_transition") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? `/responder-dispatches/${encodeURIComponent(id)}/transition` : "";
    }
    return "";
  }
}

/** Operator-facing bus / act errors — never paths, codes, or stacks. */
export function softPersonErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not in your scope|subject not visible|not_visible/.test(n)) {
    return "Your account cannot do that for this person. Ask a site manager if you need access.";
  }
  if (/platform staff cannot read location|staff cannot read location/.test(n)) {
    return "Staff seats can see the roster but not handset location history.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|device_not_found/.test(n)) {
    return "That person or device is not available.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|disabled|unavailable|not armed/.test(n)) {
    return "Dispatch or tracking is not armed on this site right now.";
  }
  if (/consent|revoked/.test(n)) {
    return "Location is not shared for this person.";
  }
  if (/on_scene|transition|refused|invalid status/.test(n)) {
    return "That status change is not allowed for this dispatch.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/personnel|\/devices|\/responder|http|get |post |status_path|sql|traceback|page\d+/.test(n)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

export function softPersonSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: dual seat + limits from Live posture (Page225). */
export function opsPersonSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const limits = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const trackMax = Math.max(1, Number(limits.track_max || 200) || 200);
  const trackDefault = Math.min(trackMax, Math.max(1, Number(limits.track_default || 100) || 100));
  const assignMax = Math.max(1, Number(limits.assign_targets_max || 6) || 6);
  const regMax = Number(limits.register_paint_max);
  const trackAllowed = (p.track_plane_allowed === true || p.track_allowed === true);
  const actAllowed = (p.act_plane_allowed === true || p.dispatch_write_allowed === true);
  const roster = (p.roster_list_allowed === true);
  return {
    rosterAllowed: roster,
    trackAllowed,
    registerAllowed: (p.register_read_allowed === true),
    dispatchWrite: actAllowed,
    cancelWrite: (p.cancel_write_allowed === true),
    reReadAllowed: (p.re_read_allowed === true),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    trackLimit: trackDefault,
    assignMax,
    registerPaintMax:
      Number.isFinite(regMax) && regMax > 0 ? Math.floor(regMax) : 128,
    isolation: p.isolation || null,
  };
}

/** Pure: flatten GET /personnel. */
export function opsPersonRosterOf(pack) {
  const rows = (pack && pack.items) || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const labels = personnelLiveMapRowOf(row) || {
      id: String(row.username || "").trim(),
      name: String(row.username || "").trim(),
      callsign: "",
      site: "",
      crew: "",
      registerProfileLive: false,
    };
    const pos = row.position || null;
    const asg = row.assignment || null;
    return {
      id: labels.id,
      name: labels.name,
      callsign: labels.callsign || "",
      site: labels.site || "",
      crew: labels.crew || "",
      registerProfileLive: labels.registerProfileLive === true,
      role: String(row.role || ""),
      phone: String(row.phone || ""),
      avatar: row.avatar_url || null,
      ll: pos && pos.lng != null && pos.lat != null
        ? [Number(pos.lng), Number(pos.lat)]
        : null,
      acc: pos && pos.accuracy_m != null ? Number(pos.accuracy_m) : null,
      age: pos && pos.age_s != null ? Number(pos.age_s) : null,
      stale: pos ? !!pos.stale : null,
      onCall: row.on_call == null ? null : !!row.on_call,
      consent: String(row.consent || "none"),
      state: asg ? String(asg.status || "") : pos ? "idle" : "invisible",
      disp: asg ? asg.dispatch_id : null,
      dispDevice: asg ? asg.device_id : null,
    };
  }).filter((x) => x.id);
}

/** Pure: flatten GET /devices for assign rank. */
export function opsPersonDevicesOf(pack) {
  const rows = (pack && (pack.items || pack.devices)) || [];
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((r) => {
    if (!r) return null;
    const id = String(r.device_id || r.id || "").trim();
    if (!id) return null;
    const lat = r.map_lat == null ? null : Number(r.map_lat);
    const lng = r.map_lng == null ? null : Number(r.map_lng);
    const hasPos = Number.isFinite(lat) && Number.isFinite(lng);
    const kind = String(r.kind || r.device_kind || "device").toLowerCase() || "device";
    return {
      id,
      label: boardLabelSay(r, id),
      kind,
      zone: String(r.zone || "").trim(),
      ll: hasPos ? [lng, lat] : null,
    };
  }).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/** Pure: great-circle metres between [lng,lat] pairs. */
export function metresBetween(a, b) {
  if (!a || !b) return null;
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(a[0] - b[0]);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

/** Pure: assign rank list — distance only, no Soft skills invent. */
export function assignTargetsOf(devices, personLl, limit = 6) {
  const list = Array.isArray(devices) ? devices : [];
  const n = Math.max(1, Number(limit) || 6);
  if (!personLl) return [];
  return list
    .filter((d) => d && d.ll && d.kind !== "gateway")
    .map((d) => ({
      id: d.id,
      label: d.label || boardLabelSay(null, d.id),
      zone: d.zone || "—",
      kind: d.kind || "device",
      m: metresBetween(personLl, d.ll),
    }))
    .filter((d) => d.m != null)
    .sort((a, b) => a.m - b.m)
    .slice(0, n);
}

/** Pure: map track payload → points. */
export function trackPointsOf(payload) {
  const rows = (payload && payload.items) || [];
  return rows
    .map((x) => [
      Number(x.longitude),
      Number(x.latitude),
      formatUserInstant(x.recorded_at, { date: false }),
      Number(x.accuracy_m),
    ])
    .filter((t) => Number.isFinite(t[0]) && Number.isFinite(t[1]))
    .reverse();
}

/**
 * Person track poll — only while Trail tab / map trail layer is on.
 * Server audits each access; never Soft-load on card open alone.
 */
export function usePersonnelTrack(username, active, limit = 100, range = null) {
  const query = useMemo(
    () => personnelTrackQueryOf({
      limit,
      since: range && range.since,
      until: range && range.until,
    }),
    [limit, range && range.since, range && range.until],
  );
  const p = active && username
    ? safePath("get__personnel_p_track", {
      pathParams: [username],
      query,
    })
    : "";
  const r = useApi(p || "");
  const pts = useMemo(() => trackPointsOf(r.data), [r.data]);
  return { pts, loading: r.loading, error: r.error, reload: r.reload };
}

export function OpsPersonSheetDesk({ id, onClose, trail, setTrail, toast, centre, initialTab }) {
  const posture = useApi(safePath("get__ops_person_status"));
  const post = posture.data || {};
  const seat = opsPersonSeatOf(
    posture.loading || !posture.data
      ? {
          track_plane_allowed: false,
          act_plane_allowed: false,
          roster_list_allowed: false,
          track_allowed: false,
          register_read_allowed: false,
          dispatch_write_allowed: false,
          re_read_allowed: false,
          limits: {
            track_default: 100,
            track_max: 200,
            assign_targets_max: 6,
            register_paint_max: 128,
          },
        }
      : post,
  );
  const peopleR = useApi(seat.rosterAllowed ? safePath("get__personnel") : "");
  const policyR = useApi(
    seat.trackAllowed
      ? (safePath("get__personnel_policy") || "/personnel/policy")
      : "",
  );
  const policy = policyR.data && typeof policyR.data === "object" ? policyR.data : null;
  const trackArmed = policy ? policy.track_available === true : null;
  // Soft≠Live: always bound plant paint for assign rank (sa_staff fleets).
  const devicesR = useApi(
    seat.registerAllowed
      ? safePath("get__devices", {
          query: { limit: seat.registerPaintMax, skip_cache: 1 },
        })
      : "",
  );
  const people = useMemo(() => opsPersonRosterOf(peopleR.data), [peopleR.data]);
  const devices = useMemo(() => {
    const rows = opsPersonDevicesOf(devicesR.data);
    return rows.slice(0, seat.registerPaintMax);
  }, [devicesR.data, seat.registerPaintMax]);
  const gate = centre || (devices.length
    ? (() => {
      const pts = devices.map((d) => d.ll).filter(Boolean);
      if (!pts.length) return null;
      return [
        pts.reduce((a, p) => a + p[0], 0) / pts.length,
        pts.reduce((a, p) => a + p[1], 0) / pts.length,
      ];
    })()
    : null);
  const p = people.find((x) => x && x.id === id);
  const startTab = initialTab === "assign" || initialTab === "trail" ? initialTab : "who";
  const [tab, setTab] = useState(startTab);
  useEffect(() => {
    setTab(initialTab === "assign" || initialTab === "trail" ? initialTab : "who");
  }, [id, initialTab]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trackSince, setTrackSince] = useState("");
  const [trackUntil, setTrackUntil] = useState("");
  const trackRange = useMemo(
    () => ({ since: trackSince, until: trackUntil }),
    [trackSince, trackUntil],
  );
  const trackWanted = seat.trackAllowed && (tab === "trail" || !!trail);
  const track = usePersonnelTrack(
    id,
    trackWanted && trackArmed === true,
    seat.trackLimit,
    trackRange,
  );

  const reRead = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    peopleR.reload && peopleR.reload();
    if (seat.trackAllowed) policyR.reload && policyR.reload();
    devicesR.reload && devicesR.reload();
    if (trackArmed === true) track.reload && track.reload();
    toast && toast("Person refreshed", "go");
  };

  const shellClass = "psheet glass ops-person-desk ops-person-desk--liquid";
  const seatLabel = softPersonSeatLabelOf(seat.role || post.principal_role);

  if (!posture.loading && post.enabled === false) {
    return html`
      <${M.aside} className=${shellClass}
        data-cmc-page="page145-person" data-cmc-legacy="page67-person"
        data-cmc-residual="page225-person"
        initial=${{ x: 34, opacity: 0 }} animate=${{ x: 0, opacity: 1 }}
        exit=${{ x: 28, opacity: 0 }} transition=${SOFT} onClick=${stop}>
        <div className="psheet__in">
          <div className="ops-person-desk__rail">
            <div>
              <p className="eyebrow">Person</p>
              <h2 className="ops-person-desk__title">Seat closed</h2>
            </div>
            <button type="button" className="x" onClick=${onClose} aria-label="Close">
              <${Ico} d="close" w=${13}/></button>
          </div>
          <${Alert} tone="warn" title="Person closed"
            say="This seat cannot open person details right now."}/>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.reReadAllowed}
            onClick=${reRead}>
            Refresh</button>
        </div>
      <//>`;
  }

  if (!p) {
    return html`
      <${M.aside} className=${shellClass}
        data-cmc-page="page145-person" data-cmc-legacy="page67-person"
        data-cmc-residual="page225-person"
        initial=${{ x: 34, opacity: 0 }} animate=${{ x: 0, opacity: 1 }}
        exit=${{ x: 28, opacity: 0 }} transition=${SOFT} onClick=${stop}>
        <div className="psheet__in">
          <div className="ops-person-desk__rail">
            <div>
              <p className="eyebrow">Person</p>
              <h2 className="ops-person-desk__title">Not on the roster</h2>
            </div>
            <button type="button" className="x" onClick=${onClose} aria-label="Close">
              <${Ico} d="close" w=${13}/></button>
          </div>
          <${Alert} tone="warn" title="Not on the roster"
            say=${"“" + String(id || "—") + "” is not on your roster."}/>
          <div className="btn-row" style=${{ marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!seat.reReadAllowed}
              onClick=${reRead}>
              Refresh roster</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => go("/response/roster")}>Open response roster</button>
          </div>
        </div>
      <//>`;
  }

  const pts = track.pts;
  const stale = (p.stale === true);
  const targets = assignTargetsOf(devices, p.ll, seat.assignMax);
  const fromCentre = p.ll && gate ? metresBetween(p.ll, gate) : null;

  const dispatchHere = async () => {
    if (!target || busy || !seat.dispatchWrite) {
      if (!seat.dispatchWrite) {
        toast && toast("Dispatch needs a site-manager seat", "warn");
      }
      return;
    }
    setBusy(true);
    try {
      await api(safePath("post__responder-dispatches"), {
        method: "POST",
        body: {
          responder_username: p.id,
          device_id: target,
          reason: "Map person sheet — send responder",
        },
      });
      toast && toast("Sent · " + (p.name || p.id) + " → unit", "go");
      reRead();
    } catch (e) {
      toast && toast(softPersonErrorOf(e && (e.message || e.detail || e)), "bad");
    } finally {
      setBusy(false);
    }
  };

  const cancelDisp = async () => {
    if (!p.disp || busy || !seat.cancelWrite) {
      if (!seat.cancelWrite) {
        toast && toast("Cancel needs a site-manager seat", "warn");
      }
      return;
    }
    setBusy(true);
    try {
      await api(safePath("post__responder-dispatches_p_transition", {
        pathParams: [p.disp],
      }), {
        method: "POST",
        body: { status: "cancelled" },
      });
      toast && toast("Dispatch cancelled", "go");
      reRead();
    } catch (e) {
      toast && toast(softPersonErrorOf(e && (e.message || e.detail || e)), "bad");
    } finally {
      setBusy(false);
    }
  };

  return html`
    <${M.aside} className=${shellClass}
      data-cmc-page="page145-person" data-cmc-legacy="page67-person"
      data-cmc-residual="page225-person"
      initial=${{ x: 34, opacity: 0 }} animate=${{ x: 0, opacity: 1 }}
      exit=${{ x: 28, opacity: 0 }} transition=${SOFT} onClick=${stop}>
      <div className="psheet__in">
        <div className="ops-person-desk__rail">
          <div className="psheet__h" style=${{ marginBottom: 0, flex: 1 }}>
            <span className="pav" data-role=${p.role} data-oncall=${p.onCall === true ? "1" : "0"}>
              <b>${initialsOf(p.id)}</b>
              <i><${Ico} d=${ROLE_GLYPH[p.role] || "user"} w=${10}/></i>
            </span>
            <span className="psheet__id">
              <b>${p.name || (looksLikeCode(p.id) ? "Person on this site" : p.id)}</b>
              <em>${p.role || "—"} · ${p.onCall == null ? "on-call unknown" : p.onCall ? "on call" : "off call"} · ${operatorSay(p.state, "—")}</em>
            </span>
          </div>
          <button type="button" className="x" onClick=${onClose} aria-label="Close">
            <${Ico} d="close" w=${13}/></button>
        </div>
        <p className="ops-person-desk__sub">On map · ${seatLabel}</p>

        <div className="ops-person-desk__planes" role="group" aria-label="Person access">
          <div className="ops-person-desk__plane" data-on=${seat.trackAllowed ? "1" : "0"}>
            <span>Location</span>
            <b>${seat.trackAllowed ? "open" : "sealed"}</b>
            <em>trail ≤${seat.trackLimit}</em>
          </div>
          <div className="ops-person-desk__plane" data-on=${seat.dispatchWrite ? "1" : "0"}>
            <span>Dispatch</span>
            <b>${seat.dispatchWrite ? "open" : "sealed"}</b>
            <em>${seat.dispatchWrite ? "send allowed" : "manager only"}</em>
          </div>
        </div>

        <div className="btn-row" style=${{ marginBottom: 10 }}>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.reReadAllowed}
            onClick=${reRead}>
            <${Ico} d="refresh" w=${13}/> Refresh
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/response/roster")}>Response roster</button>
        </div>

        <a className="pcall" href=${"tel:" + (p.phone || "").replace(/\s/g, "")}>
          <${Ico} d="phone" w=${13}/>
          <span className="pcall__n">${p.phone || "no number on file"}</span>
          <em>call · handset</em>
        </a>

        ${!p.ll ? html`
          <${Alert} tone="bad" icon="ban" title="No position yet"
            say=${p.consent === "revoked"
              ? "Consent revoked · positions cleared."
              : p.consent === "none"
              ? "No location consent."
              : "Consent granted · no fix yet."}/>`
        : html`<${F}>
          <div className="psheet__tabs" role="tablist">
            ${[["who", "Position", true], ["assign", "Assign", true], ["trail", "Trail", seat.trackAllowed]].map(([k, l, open]) => html`
              <button key=${k} type="button" role="tab" data-on=${tab === k ? "1" : "0"}
                data-dim=${open ? "0" : "1"}
                disabled=${!open}
                title=${open ? l : l + " · sealed for this seat"}
                onClick=${() => open && setTab(k)}>${l}</button>`)}
          </div>

          ${tab === "who" ? html`<${F}>
            <${KV} k="Accuracy" v=${Number.isFinite(p.acc) ? "± " + p.acc + " m" : UNKNOWN}/>
            <${KV} k="Fix age" v=${Number.isFinite(p.age) ? Math.round(p.age) + " s" : UNKNOWN}
              tone=${stale ? "warn" : ""}/>
            <${KV} k="Source" v="phone"/>
            <${KV} k="Consent" v=${p.consent || "—"}/>
            <${KV} k="From site centre" v=${fromCentre != null ? fromCentre + " m" : UNKNOWN}/>
            ${p.disp ? html`<${KV} k="Open dispatch" v=${p.disp}/>` : null}
            <p className="note">Battery and ETA not reported.</p>
            <p className="note">Accuracy is handset-reported.</p>
          <//>` : null}

          ${tab === "assign" ? html`<${F}>
            <p className="sub">Nearest boards by map distance only · top ${seat.assignMax}. Time and skills are not guessed.</p>
            ${!seat.dispatchWrite
              ? html`<${Alert} tone="info" title="Dispatch sealed"
                  say="Sending a person needs a site manager. You can still see the nearest units when tracking is open."/>`
              : null}
            <div className="assign">
              ${targets.length ? targets.map((d) => html`
                <button key=${d.id} type="button" className="assign__r"
                  data-on=${target === d.id ? "1" : "0"}
                  onClick=${() => setTarget(d.id)}>
                  <${Ico} d=${d.kind} w=${13}/>
                  <span className="assign__t"><b>${d.label}</b><em>${d.zone}</em></span>
                  <span className="assign__d">${d.m} m</span>
                </button>`)
              : html`<p className="note">No positioned devices for rank.</p>`}
            </div>
            <div className="btn-row" style=${{ marginTop: 12 }}>
              <button type="button" className="btn btn--go btn--sm"
                disabled=${!target || busy || !seat.dispatchWrite} onClick=${dispatchHere}>
                Dispatch</button>
              ${p.disp ? html`
                <button type="button" className="btn btn--ghost btn--sm"
                  disabled=${busy || !seat.cancelWrite} onClick=${cancelDisp}>
                  Cancel dispatch</button>` : null}
            </div>
            <p className="note">
              Cancel stops a run that is still en route. After the person is on scene, cancel is refused.
            </p>
          <//>` : null}

          ${tab === "trail" ? html`<${F}>
            <label className="onoff" style=${{ marginBottom: 10 }}>
              <span className="sw sw--sm" data-on=${trail ? "1" : "0"}
                onClick=${() => setTrail && setTrail(!trail)}><i/></span>
              <b>Draw trail on map</b>
            </label>
            ${trackArmed === true ? html`
              <div className="mux__form mux__form--track-range" data-res-gap="track-range" style=${{ marginBottom: 10 }}>
                <${F} label="Since (local)">
                  <input type="datetime-local" className="input" value=${trackSince}
                    onInput=${(e) => setTrackSince(e.target.value)}/>
                <//>
                <${F} label="Until (local)">
                  <input type="datetime-local" className="input" value=${trackUntil}
                    onInput=${(e) => setTrackUntil(e.target.value)}/>
                <//>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => { setTrackSince(""); setTrackUntil(""); }}>
                  Clear window
                </button>
              </div>` : null}
            ${!seat.trackAllowed
              ? html`<${Alert} tone="info" title="Trail sealed"
                  say="Location history is not open for this seat."/>`
              : null}
            ${seat.trackAllowed && trackArmed === false
              ? html`<${Alert} tone="info" title="History dark · not armed"
                  say="Deployment policy keeps location history dark — trail reads stay off until history and batch are armed."/>`
              : null}
            ${track.loading ? html`<p className="sub">Reading trail…</p>` : null}
            ${trackArmed === true && track.error ? html`<${Alert} tone="bad" icon="ban"
              title="Trail not available"
              say=${softPersonErrorOf(track.error)}/>` : null}
            <div className="trail">
              ${[...pts].reverse().map((t, i) => html`
                <div key=${t[2] + ":" + i} className="trail__r" data-first=${i === 0 ? "1" : "0"}>
                  <i/><code>${t[2]}</code>
                  <span>${Number.isFinite(t[3]) ? "± " + t[3] + " m" : UNKNOWN}</span>
                  <em>${i === pts.length - 1 ? "first fix held"
                    : (() => {
                      const prev = pts[pts.length - 1 - i + 1];
                      if (!prev) return "—";
                      const m = metresBetween([t[0], t[1]], [prev[0], prev[1]]);
                      return m != null ? m + " m moved" : "—";
                    })()}</em>
                </div>`)}
            </div>
            ${!track.loading && trackArmed === true && !track.error && !pts.length ? html`
              <p className="note">Empty trail.</p>` : null}
            <p className="note">
              ${pts.length} fix${pts.length === 1 ? "" : "es"}. Opening the trail is recorded · consent revoke clears history.
            </p>
          <//>` : null}
        <//>`}
      </div>
    <//>`;
}
